import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

const yen = (n) => (n == null ? '' : '¥' + Number(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
const num = (n) => (n == null ? '' : Number(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
const jd = (iso) => (iso ? String(iso).slice(0, 10).replaceAll('-', '/') : '');
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/settings/logo`;
const SEAL_URL = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/settings/seal`;
const MIN_ROWS = 10;

export const SHEET_CSS = `
  .mkt-sheet { width: 210mm; min-height: 295mm; margin: 0 auto; background: #fff; color: #1c1c1e;
    padding: 15mm 15mm 12mm; font-family: 'Hiragino Kaku Gothic ProN', 'Yu Gothic', 'Noto Sans JP', sans-serif;
    font-size: 10pt; line-height: 1.5; box-shadow: 0 2px 14px rgba(0,0,0,.12); box-sizing: border-box; }
  .mkt-sheet .title { text-align: center; font-size: 19pt; font-weight: 700; letter-spacing: .45em; text-indent: .45em;
    padding-bottom: 2.2mm; border-bottom: 2.5px double #1c1c1e; margin: 0 30mm 6mm; }
  .mkt-sheet .accent { height: 1mm; background: linear-gradient(90deg, #a21caf, #7c3aed); width: 34mm; margin: 0 auto 6mm; border-radius: 1mm; }
  .mkt-sheet .subtitle { text-align: center; font-size: 9.5pt; color: #3f3f46; margin: -3mm 0 4mm; }
  .mkt-sheet .meta { text-align: right; font-size: 8.5pt; color: #3f3f46; }
  .mkt-sheet .parties { display: flex; justify-content: space-between; gap: 10mm; margin-top: 4mm; }
  .mkt-sheet .cust { flex: 1; min-width: 0; }
  .mkt-sheet .cust-name { font-size: 13pt; font-weight: 700; border-bottom: 1.5px solid #1c1c1e; padding-bottom: 1mm; margin-bottom: 1.5mm; }
  .mkt-sheet .comp { width: 62mm; font-size: 8.5pt; color: #27272a; }
  .mkt-sheet .comp-body { position: relative; }
  .mkt-sheet .comp-name { font-size: 11pt; font-weight: 700; margin-bottom: .8mm; }
  .mkt-sheet .comp img.logo { max-height: 11mm; max-width: 50mm; object-fit: contain; margin-bottom: 1.5mm; display: block; }
  .mkt-sheet .comp img.seal { position: absolute; top: -2mm; right: 0; width: 14mm; height: 14mm; object-fit: contain; opacity: .85; }
  .mkt-sheet .greet { margin-top: 4mm; font-size: 9.5pt; }
  .mkt-sheet .total-band { display: flex; align-items: baseline; gap: 6mm; margin: 4mm 0 5mm;
    border-bottom: 2px solid #1c1c1e; padding-bottom: 1.5mm; width: 120mm; }
  .mkt-sheet .total-band .lbl { font-size: 10.5pt; font-weight: 600; }
  .mkt-sheet .total-band .val { font-size: 16pt; font-weight: 700; letter-spacing: .02em; }
  .mkt-sheet .total-band .tax-note { font-size: 8pt; color: #71717a; }
  .mkt-sheet table.lines { width: 100%; border-collapse: collapse; font-size: 9pt; }
  .mkt-sheet table.lines th { background: #f4f4f5; border: 1px solid #a1a1aa; padding: 1.6mm 2mm; font-weight: 600; font-size: 8.5pt; }
  .mkt-sheet table.lines td { border: 1px solid #a1a1aa; padding: 1.6mm 2mm; height: 6.4mm; }
  .mkt-sheet .r { text-align: right; font-variant-numeric: tabular-nums; }
  .mkt-sheet .ctr { text-align: center; }
  .mkt-sheet .sku { font-size: 7.5pt; color: #71717a; font-family: ui-monospace, monospace; }
  .mkt-sheet table.sum { border-collapse: collapse; font-size: 9.5pt; margin-left: auto; margin-top: -1px; }
  .mkt-sheet table.sum th { background: #f4f4f5; border: 1px solid #a1a1aa; padding: 1.6mm 3mm; font-weight: 600; width: 30mm; }
  .mkt-sheet table.sum td { border: 1px solid #a1a1aa; padding: 1.6mm 3mm; width: 34mm; text-align: right; font-variant-numeric: tabular-nums; }
  .mkt-sheet .box { border: 1px solid #a1a1aa; padding: 2mm 3mm; margin-top: 4mm; min-height: 14mm; font-size: 8.5pt; }
  .mkt-sheet .box .box-lbl { font-size: 7.5pt; color: #71717a; margin-bottom: .5mm; }
  .mkt-sheet .info { display: flex; flex-wrap: wrap; gap: 2mm 10mm; margin-top: 3mm; font-size: 9pt; }
  .mkt-sheet .info .kv { display: flex; gap: 3mm; }
  .mkt-sheet .info .k { color: #71717a; font-size: 8pt; padding-top: .4mm; }
  .mkt-sheet .foot { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 5mm; font-size: 7.5pt; color: #a1a1aa; }
  @media print {
    .mkt-sheet { box-shadow: none; margin: 0; width: auto; min-height: auto; }
    @page { size: A4 portrait; margin: 0; }
  }
`;

// 会社ブロック（ロゴ・社印つき）— 見積書/請求書/月次請求書で共通
// スナップショットに file_id があればそれを優先（書類発行時点の画像を維持）
export function CompanyBlock({ s, co }) {
  const [logoOk, setLogoOk] = useState(true);
  const [sealOk, setSealOk] = useState(true);
  const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/marketing/files/${id}`;
  const logoSrc = s.logo_file_id ? fileUrl(s.logo_file_id) : LOGO_URL;
  const sealSrc = s.seal_file_id ? fileUrl(s.seal_file_id) : SEAL_URL;
  return (
    <div className="comp">
      {s.has_logo && logoOk && (
        <img className="logo" src={logoSrc} alt="logo" onError={() => setLogoOk(false)} />
      )}
      <div className="comp-body">
        <div className="comp-name">{co.name || ''}</div>
        {(co.zip || co.address) && <div>{co.zip ? `〒${String(co.zip).replace('〒', '')} ` : ''}{co.address || ''}</div>}
        {(co.tel || co.fax) && <div>{co.tel ? `TEL: ${co.tel}` : ''}{co.tel && co.fax ? '　' : ''}{co.fax ? `FAX: ${co.fax}` : ''}</div>}
        {co.invoice_reg_no && <div>登録番号: {co.invoice_reg_no}</div>}
        {(s.sales_rep_name || co.contact) && <div>担当: {s.sales_rep_name || co.contact}</div>}
        {s.has_seal && sealOk && (
          <img className="seal" src={sealSrc} alt="社印" onError={() => setSealOk(false)} data-testid="mkt-sheet-seal" />
        )}
      </div>
    </div>
  );
}

function useQr(text) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!text) return;
    QRCode.toDataURL(text, { margin: 0, width: 100 }).then(setUrl).catch(() => setUrl(''));
  }, [text]);
  return url;
}

// 見積書 / 請求書 A4 print sheet — renders ONLY from the immutable mkt_documents snapshot.
export default function QuotationSheet({ doc }) {
  const qr = useQr(doc ? `${window.location.origin}/marketing/orders/${doc.order_id}` : '');
  if (!doc) return null;
  const s = doc.snapshot || {};
  const isQuote = doc.doc_type === 'quotation';
  const title = isQuote ? '御 見 積 書' : '御 請 求 書';
  const c = s.customer || {};
  const co = s.company || {};
  const lines = s.lines || [];
  const rows = [...lines];
  while (rows.length < MIN_ROWS) rows.push(null);

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div className="mkt-sheet" data-testid="mkt-doc-sheet">
        <div className="meta">
          <div>{isQuote ? '見積番号' : '請求番号'}: <b>{doc.doc_no}</b></div>
          <div>発行日: {jd(doc.issued_at)}</div>
          {isQuote && doc.valid_until && <div>有効期限: {jd(doc.valid_until)}</div>}
        </div>
        <h1 className="title">{title}</h1>
        <div className="accent" />

        <div className="parties">
          <div className="cust">
            <div className="cust-name">{c.name || ''}　{c.honorific || '御中'}</div>
            {(c.zip || c.address) && <div style={{ fontSize: '8.5pt', color: '#3f3f46' }}>{c.zip ? `〒${String(c.zip).replace('〒', '')}　` : ''}{c.address || ''}</div>}
            {c.contact && <div style={{ fontSize: '8.5pt', color: '#3f3f46' }}>ご担当: {c.contact} 様</div>}
            <div className="greet">{isQuote ? '下記の通りお見積り申し上げます。' : '下記の通りご請求申し上げます。'}</div>
            <div className="total-band">
              <span className="lbl">{isQuote ? '御見積金額 合計' : '御請求金額 合計'}</span>
              <span className="val">{yen(s.total)}</span>
              <span className="tax-note">（税込）</span>
            </div>
            <div className="info">
              {s.title && <div className="kv"><span className="k">件名</span><span>{s.title}</span></div>}
              {s.due_date && <div className="kv"><span className="k">{isQuote ? '受渡期日' : '納品日'}</span><span>{jd(s.due_date)}</span></div>}
              {c.payment_terms && <div className="kv"><span className="k">お支払条件</span><span>{c.payment_terms}</span></div>}
              {isQuote && doc.valid_until && <div className="kv"><span className="k">見積有効期限</span><span>{jd(doc.valid_until)}</span></div>}
            </div>
          </div>
          <CompanyBlock s={s} co={co} />
        </div>

        <div style={{ marginTop: '4mm' }}>
          <table className="lines">
            <colgroup>
              <col style={{ width: '8mm' }} /><col /><col style={{ width: '17mm' }} />
              <col style={{ width: '12mm' }} /><col style={{ width: '25mm' }} /><col style={{ width: '28mm' }} />
            </colgroup>
            <thead>
              <tr><th>No.</th><th>品名 ・ 品番</th><th>数量</th><th>単位</th><th>単価</th><th>金額</th></tr>
            </thead>
            <tbody>
              {rows.map((ln, i) => (
                <tr key={i}>
                  <td className="ctr">{ln ? i + 1 : '\u00A0'}</td>
                  <td>{ln ? <>{ln.name}{ln.sku && <span className="sku">　{ln.sku}</span>}{ln.notes && <span className="sku">　{ln.notes}</span>}</> : '\u00A0'}</td>
                  <td className="r">{ln ? num(ln.qty) : ''}</td>
                  <td className="ctr">{ln ? (ln.uom || '') : ''}</td>
                  <td className="r">{ln ? yen(ln.unit_price) : ''}</td>
                  <td className="r">{ln ? yen(ln.amount) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="sum">
            <tbody>
              <tr><th>小計</th><td>{yen(s.subtotal)}</td></tr>
              <tr><th>消費税（{s.tax_rate ?? 10}%）</th><td>{yen(s.tax)}</td></tr>
              <tr><th style={{ fontWeight: 700 }}>合計</th><td style={{ fontWeight: 700 }}>{yen(s.total)}</td></tr>
            </tbody>
          </table>
        </div>

        {!isQuote && co.bank && (
          <div className="box">
            <div className="box-lbl">お振込先</div>
            <div style={{ whiteSpace: 'pre-line' }}>{co.bank}</div>
          </div>
        )}
        <div className="box">
          <div className="box-lbl">備考</div>
          <div style={{ whiteSpace: 'pre-line' }}>{s.notes || ''}</div>
        </div>

        <div className="foot">
          <span>受注番号: {s.order_no}　／　発行者: {doc.issued_by}</span>
          {qr && <img src={qr} alt="qr" style={{ width: '12mm', height: '12mm' }} />}
        </div>
      </div>
    </>
  );
}
